import { UserCheck, FileText, Sparkles, FlaskConical, Layers } from "lucide-react";
import { statusLabel } from "@/lib/format";

const STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-700 border-slate-300", awaiting_review: "bg-blue-50 text-blue-800 border-blue-200",
  approved_for_submission: "bg-teal-100 text-teal-900 border-teal-300", ready_for_filing: "bg-teal-100 text-teal-900 border-teal-300",
  submitted: "bg-[#0B192C] text-white border-[#0B192C]", filed: "bg-[#0B192C] text-white border-[#0B192C]",
  clarification_requested: "bg-orange-50 text-orange-800 border-orange-200", search_requested: "bg-indigo-50 text-indigo-800 border-indigo-200",
  conditional_offer: "bg-green-50 text-green-800 border-green-200", published: "bg-indigo-50 text-indigo-800 border-indigo-200",
  award_setup_complete: "bg-green-50 text-green-800 border-green-200", under_examination: "bg-amber-50 text-amber-800 border-amber-200",
  active_project: "bg-green-100 text-green-900 border-green-300", granted: "bg-green-100 text-green-900 border-green-300",
  closed: "bg-slate-50 text-slate-500 border-slate-200", ended: "bg-slate-50 text-slate-500 border-slate-200",
};
const pill = "inline-flex items-center gap-1.5 rounded border px-2 py-0.5 text-xs font-medium whitespace-nowrap";

export const StatusBadge = ({ status, kind }) => (
  <span data-testid={`status-badge-${status}`} className={`${pill} ${STATUS_STYLE[status] || STATUS_STYLE.draft}`}>{statusLabel(status, kind)}</span>
);

const MATCH = {
  potential: ["Potential match", "bg-green-50 text-green-800 border-green-200"],
  further_info: ["Further information needed", "bg-yellow-50 text-yellow-800 border-yellow-200"],
  does_not_meet: ["Does not meet published criteria", "bg-red-50 text-red-800 border-red-200"],
};
export const MatchBadge = ({ status }) => (
  <span data-testid={`match-badge-${status}`} className={`${pill} ${MATCH[status]?.[1]}`}>{MATCH[status]?.[0]}</span>
);

const PROV = {
  user: [UserCheck, "User-confirmed", "bg-slate-100 text-slate-800 border-slate-300"],
  user_reviewed: [UserCheck, "Reviewed by user", "bg-slate-100 text-slate-800 border-slate-300"],
  document: [FileText, "Extracted from document", "bg-blue-50 text-blue-800 border-blue-200"],
  ai: [Sparkles, "AI suggestion", "bg-teal-50 text-teal-800 border-teal-200"],
  ai_suggestion: [Sparkles, "AI suggestion - review needed", "bg-teal-50 text-teal-800 border-teal-200"],
  assembled: [Layers, "Assembled from your answers", "bg-slate-50 text-slate-700 border-slate-200"],
};
export const ProvenanceBadge = ({ source, docName }) => {
  if (!source || !PROV[source]) return null;
  const [Icon, label, cls] = PROV[source];
  return (
    <span data-testid={`provenance-label-${source}`} className={`${pill} ${cls}`} title={docName ? `From ${docName}` : label}>
      <Icon className="h-3 w-3" aria-hidden /> {label}{docName ? `: ${docName}` : ""}
    </span>
  );
};

export const DemoBadge = ({ text = "Demonstration record" }) => (
  <span data-testid="demo-data-badge" className={`${pill} bg-amber-50 text-amber-900 border-amber-300`}>
    <FlaskConical className="h-3 w-3" aria-hidden /> {text}
  </span>
);

export const Notice = ({ tone = "info", children, testid }) => {
  const tones = { info: "border-blue-200 bg-blue-50 text-blue-900", warn: "border-amber-200 bg-amber-50 text-amber-900", danger: "border-red-200 bg-red-50 text-red-900", ok: "border-teal-200 bg-teal-50 text-teal-900" };
  return <div data-testid={testid} role="note" className={`rounded-md border px-4 py-3 text-sm leading-relaxed ${tones[tone]}`}>{children}</div>;
};

export const Empty = ({ title, children }) => (
  <div className="rounded-lg border border-dashed border-slate-300 bg-white px-6 py-10 text-center">
    <p className="font-display font-semibold text-[#0B192C]">{title}</p>
    {children && <div className="mt-2 text-sm text-slate-600">{children}</div>}
  </div>
);

export const PageHeader = ({ title, sub, children }) => (
  <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
    <div>
      <h1 className="text-2xl font-bold text-[#0B192C] sm:text-3xl">{title}</h1>
      {sub && <p className="mt-1 max-w-2xl text-sm text-slate-600">{sub}</p>}
    </div>
    {children && <div className="flex flex-wrap gap-2">{children}</div>}
  </div>
);
